import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import AgencyAPIs from "../../apiRoutes/Agency";
import { GET_AGENCIES_QUERY_KEY } from "../../utils/constants/queryKeys";
import ButtonLoader from "../Icons/ButtonLoader";
import { Button } from "../ui/button";
import AddAgencyModal from "./Modals/AddAgencyModal";
import { agencyDisplayName } from "../../utils/agencyScope";
import StatusChip from "../common/StatusChip";

const LIMIT = 20;

const AgencyTab = () => {
    const [modal, setModal] = useState({ open: false, editData: null });
    const [page, setPage] = useState(0);
    const axiosPrivate = useAxiosPrivate();

    const { data: result, isLoading } = useQuery({
        queryKey: [...GET_AGENCIES_QUERY_KEY, page],
        queryFn: () => AgencyAPIs.list({ axiosPrivate, limit: LIMIT, offset: page * LIMIT }),
    });

    const agencies = result?.data ?? [];
    const totalCount = result?.count ?? 0;

    useEffect(() => setPage(0), []);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <ButtonLoader color="#5E30BF" />
                <span className="ml-2 text-primaryPurple">Loading agencies…</span>
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-gray-800">Agencies</h3>
                <button
                    onClick={() => setModal({ open: true, editData: null })}
                    className="px-4 py-2 bg-primaryPurple text-white rounded-lg text-sm font-medium hover:bg-primaryPurple/90 transition-colors"
                >
                    Add agency
                </button>
            </div>

            <div className="flex flex-col min-h-[calc(100vh-200px)]">
                <div className="overflow-x-auto flex-1 min-h-0 rounded-xl border border-gray-200 bg-white">
                <table className="w-full text-left border-collapse">
                    <thead>
                        <tr className="border-b border-gray-200">
                            <th className="py-4 px-4 text-sm font-semibold text-gray-600">Agency</th>
                            <th className="py-4 px-4 text-sm font-semibold text-gray-600">Status</th>
                            <th className="py-4 px-4 text-sm font-semibold text-gray-600">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {agencies.length > 0 ? (
                            agencies.map((agency, i) => (
                                <tr key={agency.id} className="hover:bg-gray-50/50 transition-colors">
                                    <td className="py-4 px-4 text-sm text-gray-800 font-medium">{agencyDisplayName(agency, i)}</td>
                                    <td className="py-4 px-4 text-sm">
                                        <StatusChip subtle status={agency.status} />
                                    </td>
                                    <td className="py-4 px-4 text-sm">
                                        <button
                                            onClick={() => setModal({ open: true, editData: agency })}
                                            className="text-primaryPurple hover:underline font-medium"
                                        >
                                            Edit
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="3" className="py-12 text-center text-gray-400 italic">No agencies found.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
                </div>
            <div className="flex items-center justify-between px-4 py-2.5 border-t border-border bg-muted/30 flex-shrink-0">
                <span className="text-sm text-muted-foreground">
                    Showing {agencies.length ? page * LIMIT + 1 : 0}–{Math.min(page * LIMIT + agencies.length, totalCount)} of {totalCount}
                </span>
                <div className="flex gap-1.5">
                    <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)} className="h-8">
                        Previous
                    </Button>
                    <Button variant="outline" size="sm" disabled={page * LIMIT + agencies.length >= totalCount} onClick={() => setPage((p) => p + 1)} className="h-8">
                        Next
                    </Button>
                </div>
            </div>
            </div>

            {modal.open && (
                <AddAgencyModal
                    open={modal.open}
                    onClose={() => setModal({ open: false, editData: null })}
                    editData={modal.editData}
                />
            )}
        </div>
    );
};

export default AgencyTab;
